import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api, post, type LoginOutcome } from '../../lib/api.ts';
import { useRefreshMe } from '../../lib/auth.tsx';
import { AuthShell, Field, inputCls, btnCls, ErrorText, PasswordInput } from '../../components/AuthShell.tsx';
import { PasswordHint } from './PasswordHint.tsx';

type Inv = { email: string; role: string; tenantName: string; tenantSlug: string; expiresAt: string; hasPassword?: boolean };

/** 초대 수락: 이름·비밀번호 설정 → 로그인 */
export function InvitePage() {
  const { token = '' } = useParams();
  const nav = useNavigate();
  const refresh = useRefreshMe();
  const [inv, setInv] = useState<Inv | null>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [error, setError] = useState<unknown>(null);

  useEffect(() => { api<Inv>(`/auth/invitations/${token}`).then(setInv).catch(setError); }, [token]);

  async function submit(e: FormEvent) {
    e.preventDefault(); setError(null);
    if (!inv?.hasPassword && password !== password2) { setError(new Error('비밀번호가 서로 다릅니다')); return; }
    try {
      const r = await post<LoginOutcome>('/auth/invitations/accept', { token, name, password });
      await refresh();
      nav(r.status === 'ok' ? '/' : '/mfa/enroll', { replace: true });
    } catch (err) { setError(err); }
  }

  return (
    <AuthShell title="초대 수락" subtitle={inv?.hasPassword ? '이미 PRISM 계정이 있습니다. 기존 비밀번호로 확인하면 이 조직에 합류합니다.' : '이름과 비밀번호를 정하면 바로 시작할 수 있습니다.'} lockTenant>
      {!inv ? <div className="text-sm">{error ? <ErrorText error={error} /> : '확인 중…'}</div> : (
        <form onSubmit={submit}>
          <p className="mb-4 border border-sky-200 bg-sky-50 px-3 py-2 text-[12.5px] text-sky-900 leading-relaxed"><strong>{inv.tenantName}</strong> 워크스페이스에 <span className="font-mono">{inv.role}</span> 역할로 초대되었습니다.<br />{inv.email}</p>
          <Field label="이름"><input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} required autoFocus /></Field>
          {inv.hasPassword ? (
            <Field label="기존 계정 비밀번호" hint="비밀번호는 계정에 하나이며 모든 조직에서 같이 씁니다"><PasswordInput autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></Field>
          ) : (<>
            <Field label="비밀번호"><PasswordInput autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></Field>
            <Field label="비밀번호 확인"><PasswordInput autoComplete="new-password" value={password2} onChange={(e) => setPassword2(e.target.value)} required /></Field>
            <PasswordHint />
          </>)}
          <button className={btnCls}>시작하기</button>
          <ErrorText error={error} />
        </form>
      )}
    </AuthShell>
  );
}
